import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchGalleryBySlug } from '../cms';
import LightboxModal from '../components/LightboxModal';

export default function AboutPage() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchGalleryBySlug('om-os').then(imgs => setGalleryImages(imgs));
  }, []);

  return (
    <div className="bg-white">
      <Helmet>
        <title>Om os · TH Byg v/Jacob Hartvig Hansen</title>
        <meta name="description" content="Hos T-H Byg er kunden altid i fokus. Jeg lægger afgørende vægt på at yde en god service i øjenhøjde." />
        <meta property="og:title" content="Om os · TH Byg v/Jacob Hartvig Hansen" />
        <meta property="og:description" content="Hos T-H Byg er kunden altid i fokus. Jeg lægger afgørende vægt på at yde en god service i øjenhøjde." />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#11161a] text-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-widest text-[#d92323]">
              Erfaring & Professionelt Håndværk
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mt-2 tracking-tight">
              Om T.H Byg
            </h1>
            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              T.H Byg v/ Jacob Hartvig Hansen har leveret kvalitetsbevidst tømrerarbejde i Rask Mølle, Vejle, Horsens og Hedensted i mere end 25 år.
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 sm:py-24 bg-white text-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Om T.H Byg
              </h2>

              <p className="text-base text-slate-700 leading-relaxed">
                Mit navn er Jacob Hansen og jeg etablerede i 2009 T-H Byg. Jeg har mere end 20 års erfaring inden for tømrer og byggebranchen. Med min viden og faglige kompetencer kan jeg løse en lang række forskellige opgaver. Jeg udfører alt fra traditionelt tømrer og snedkerarbejde til totalentrepriser, heriblandt nybygning, ombygning, tilbygning samt vinduer og døre.
              </p>

              <h3 className="text-2xl font-bold text-slate-900 pt-2">
                Kunden sættes i første række
              </h3>

              <p className="text-base text-slate-700 leading-relaxed">
                Hos T-H Byg er kunden altid i fokus. Jeg lægger afgørende vægt på at yde en god service i øjenhøjde. Derfor er det vigtigt, at kunderne er med og løbende har mulighed for at blive orienteret om projektet gennem hele forløbet. Derudover lægger jeg vægt på at tidsfrister og aftaler overholdes til punkt og prikke, da det er min overbevisning, at dette udgør fundamentet for et sundt og godt samarbejde.
              </p>

              <p className="text-base text-slate-700 leading-relaxed">
                Jeg har en stor kundekreds i trekantsområdet bestående af såvel private som erhvervsdrivende, men jeg varetager også gerne opgaver for det offentlige. Uanset kunden, bestræber jeg mig altid på at levere et effektivt og professionelt stykke arbejde som jeg kan stå inde for.
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full bg-[#d92323] px-6 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#b81b1b] transition-all"
                >
                  <span>Kontakt Jacob Hartvig Hansen</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
                <a
                  href="tel:28594349"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-300 px-6 py-3.5 text-sm font-bold text-slate-800 hover:bg-slate-200 transition-all whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[#d92323]" />
                  <span className="whitespace-nowrap">(+45) 28 59 43 49</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="relative rounded-none overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-07-scaled.jpg"
                  alt="Håndværkere arbejder på ståltag i Horsens og Vejle"
                  className="w-full h-[450px] object-cover rounded-none"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages && galleryImages.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#11161a] text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-8">
            <div className="text-center max-w-xl mx-auto mb-12">
              <h2 className="text-3xl font-extrabold text-white">
                Billeder fra hverdagen
              </h2>
              <p className="mt-2 text-slate-400 text-sm">
                Klik på et billede for at se i stor visning
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow cursor-pointer h-64"
                  onClick={() => {
                    setActiveIndex(idx);
                    setLightboxOpen(true);
                  }}
                >
                  <img
                    src={img}
                    alt={`Om os billede ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setActiveIndex(idx)}
      />
    </div>
  );
}
